/*
 * Copyright (c) 2021.
 * 2iPartners SàRL and/or its affiliates.
 * All rights reserved.
 *
 * 2iPartners SàRL
 * Route de Compois 24,
 * 1252 Meinier
 * Suisse
 *
 * tel : +41 22 735 97 07
 */

import {Component, EventEmitter, Input, OnInit, Output} from '@angular/core';
import {AnimalDetail} from '../../../_models/animal-detail';
import {AbstractControl, FormControl, FormGroup, ValidationErrors, Validators} from '@angular/forms';
import {AnimalSpecies} from '../../../_models/animal-species';
import {ENTER} from '@angular/cdk/keycodes';
import {ID} from '../../../_enums/constants';
import {isControlInvalid, isControlValid, TOUCHED_OR_SUBMITTED} from '../../../_enums/form-validation';

@Component({
  selector: 'app-input-animal-number-split',
  templateUrl: './input-animal-number-split.component.html',
  styleUrls: ['./input-animal-number-split.component.scss']
})
export class InputAnimalNumberSplitComponent implements OnInit {

  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  ///                             BASE COMPONENT INPUTS                                                                                  ///
  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////

  @Input() controlName?: string;
  @Input() disableEnterKeyPress?: boolean;
  @Input() formGroup?: FormGroup;
  @Input() index?: number;
  @Input() label?: string;
  @Input() readonly ?: boolean;
  @Input() showLabel?: boolean;
  @Input() showRequired?: boolean;
  @Input() showValidation?: string;
  @Input() submitted?: boolean;

  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  ///                             CUSTOM COMPONENT INPUTS                                                                                ///
  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////

  @Input() animalDetail?: AnimalDetail;
  @Input() labelBN?: number;
  @Input() labelBO?: number;

  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  ///                             COMPONENT OUTPUTS                                                                                      ///
  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////

  @Output() updated = new EventEmitter<string>();

  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  ///                              PROPERTIES                                                                                            ///
  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////

  public bNControlName = 'breedingNumber';
  public bOControlName = 'breedingOrder';

  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  ///                              MAGICS METHODS                                                                                        ///
  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////

  constructor() {
    this.controlName = 'number';
    this.disableEnterKeyPress = false;
    this.formGroup = null;
    this.index = 0;
    this.label = null;
    this.readonly = false;
    this.showLabel = true;
    this.showRequired = true;
    this.showValidation = TOUCHED_OR_SUBMITTED;
    this.submitted = false;

    this.labelBN = null;
    this.labelBO = null;
  }

  ngOnInit() {
    if (this.formGroup) {
      const bNValidators = [Validators.pattern(this.animalDetail.breedingNumberRegex)];
      const bOValidators = [Validators.pattern(this.animalDetail.breedingOrderRegex)];

      if (!this.formGroup.contains(this.bNControlName)) {
        this.formGroup.addControl(this.bNControlName, new FormControl(null));
      }

      if (!this.formGroup.contains(this.bOControlName)) {
        this.formGroup.addControl(this.bOControlName, new FormControl(null));
      }

      if (this.required) {
        bNValidators.push(Validators.required);
        bOValidators.push(Validators.required);
      }

      this.bNControl.setValidators(bNValidators);
      this.bOControl.setValidators(bOValidators);

      this.bNControl.updateValueAndValidity();
      this.bOControl.updateValueAndValidity();
    }
  }

  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  ///                              FORM CONTROLS GETTERS                                                                                 ///
  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////

  get control(): AbstractControl | null {
    return this.formGroup ? this.formGroup.get(this.controlName) : null;
  }

  get bNControl(): AbstractControl | null {
    return this.formGroup ? this.formGroup.get(this.bNControlName) : null;
  }

  get bOControl(): AbstractControl | null {
    return this.formGroup ? this.formGroup.get(this.bOControlName) : null;
  }

  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  ///                              VALUES GETTERS                                                                                        ///
  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////

  get animalSpecies(): AnimalSpecies | null {
    return (this.animalDetail && this.animalDetail._embedded) ? this.animalDetail._embedded.animalSpecies : null;
  }

  get bNControlId(): string {
    return `${this.bNControlName}_${this.index}_${ID}`;
  }

  get bOControlId(): string {
    return `${this.bOControlName}_${this.index}_${ID}`;
  }

  get bNRequired(): boolean {
    return this.required;
  }

  get bORequired(): boolean {
    return this.required;
  }

  get bNShowInvalid(): boolean {
    return isControlInvalid(this.bNControl, this.showValidation, this.submitted);
  }

  get bNShowValid(): boolean {
    return isControlValid(this.bNControl, this.showValidation);
  }

  get bOShowInvalid(): boolean {
    return isControlInvalid(this.bOControl, this.showValidation, this.submitted);
  }

  get bOShowValid(): boolean {
    return isControlValid(this.bOControl, this.showValidation);
  }

  get controlId(): string {
    return `${this.controlName}_${this.index}_${ID}`;
  }

  get getAnimalNumber(): string {
    const breedingNumber = (this.bNControl && this.bNControl.value) ? this.bNControl.value : '';
    const breedingOrder = (this.bOControl && this.bOControl.value) ? this.bOControl.value : '';
    return `${breedingNumber}${this.splitDelimiter}${breedingOrder}`;
  }

  get required(): boolean {
    return this.validators && this.validators.hasOwnProperty('required');
  }

  get showInvalid(): boolean {
    return isControlInvalid(this.control, this.showValidation, this.submitted);
  }

  get showValid(): boolean {
    return isControlValid(this.control, this.showValidation);
  }

  get splitDelimiter(): string {
    return (this.animalSpecies) ? this.animalSpecies.splitDelimiter : '';
  }

  get validators(): ValidationErrors | null {
    return this.control && this.control.validator && this.control.validator(this.control);
  }

  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  ///                              CHANGE LISTENERS                                                                                      ///
  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////

  public onChange(e): void {
    if (e && e.target) {
      this.control.setValue(this.getAnimalNumber);
      this.updated.emit(this.getAnimalNumber);
    }
  }

  public keypress(e): void {
    if (e.keyCode === ENTER && this.disableEnterKeyPress) {
      e.preventDefault();
    }
  }

}
